import React from 'react';
import { Drawer, Button, IconButton, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { Close } from '@mui/icons-material';
import { useTheme } from '@mui/system';
import { useGetUserData } from '@hooks/useGetUserData';
import { useSignUpModal } from '@hooks/useLoginModal';
import { DealPostType } from '@types';
import ShowIcon from '@components/ShowIcon';
import FilterDrawer from '../FilterDrawer';
import { FilterFunctionsType } from '../Filters/utils';

const MobileFilter = ({
  setDrawerOpen,
  drawerOpen,
  hasFilters,
  resetAll,
  showingStr,
  funcs,
  latestDealsLoading,
  unfilteredDealsWithASIN
}: {
  setDrawerOpen: (open: boolean) => void;
  drawerOpen: boolean;
  hasFilters: boolean;
  resetAll: () => void;
  showingStr: string;
  funcs: FilterFunctionsType;
  latestDealsLoading: boolean;
  unfilteredDealsWithASIN: DealPostType[];
}) => {
  const theme = useTheme();
  const { data: user } = useGetUserData();
  const { showNonLoggedInModal } = useSignUpModal();
  const isDarkMode = theme.palette.mode === 'dark';
  return (
    <Box
      display={{
        xs: 'block',
        md: 'none'
      }}
    >
      <Box
        display="flex"
        ml="-5px"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          height: '32px',
          mb: 1

          // if less than 310 pixels, then justify content to end
          // '@media (max-width: 310px)': {
          //   justifyContent: 'end'
          // }
        }}
      >
        <Box
          data-testid="mobile-filter-chips"
          sx={{
            display: 'flex',
            // vertically middle align content
            alignItems: 'center',
            /* shorthand for flexGrow:1, flexShrink:1, flexBasis:0 */
            flex: '1 1 0px',
            minWidth: 0, // allow it to shrink below its content
            overflowX: 'auto', // make overflow scrollable
            WebkitOverflowScrolling: 'touch',
            height: '32px',
            // remove scroll bar
            scrollbarWidth: 'none', // for Firefox
            msOverflowStyle: 'none', // for Internet Explorer and Edge
            '&::-webkit-scrollbar': {
              display: 'none' // for Chrome, Safari, and Opera
            }
          }}
        >
          <Chip
            label="Popular"
            size="small"
            icon={
              <ShowIcon
                sx={{
                  color: funcs.trending
                    ? '#EEA264 !important'
                    : '#C25700 !important'
                }}
                icon="InsightsIcon"
              />
            }
            onClick={() => funcs.setTrending(!funcs.trending)}
            clickable
            color={funcs.trending ? 'primary' : 'default'}
            sx={{
              mr: '4px',
              backgroundColor: funcs.trending ? 'primary.main' : 'grey.300',
              color: funcs.trending ? 'white' : 'black'
              // hide if width is less than 310 pixels
              // '@media (max-width: 310px)': {
              //   display: 'none'
              // }
            }}
          />
          <Chip
            label="Coupons"
            size="small"
            icon={
              <ShowIcon
                sx={{
                  color: funcs.onlyCoupons
                    ? '#67a9f4 !important'
                    : `${theme.palette.couponColor} !important`
                }}
                icon="LocalOfferIcon"
              />
            }
            onClick={() => funcs.setOnlyCoupons(!funcs.onlyCoupons)}
            clickable
            color={funcs.onlyCoupons ? 'primary' : 'default'}
            sx={{
              mr: '4px',
              backgroundColor: funcs.onlyCoupons ? 'primary.main' : 'grey.300',
              color: funcs.onlyCoupons ? 'white' : 'black'
              // hide if width is less than 410 pixels
              // '@media (max-width: 410px)': {
              //   display: 'none'
              // }
            }}
          />
          <Chip
            label="Subscribe & Save"
            size="small"
            icon={
              <ShowIcon
                sx={{
                  color: funcs.onlySubscribeSave
                    ? '#4ddfce !important'
                    : `#00796b !important`
                }}
                icon="AutorenewIcon"
              />
            }
            onClick={() => funcs.setOnlySubscribeSave(!funcs.onlySubscribeSave)}
            clickable
            color={funcs.onlySubscribeSave ? 'primary' : 'default'}
            sx={{
              mr: '4px',
              backgroundColor: funcs.onlySubscribeSave
                ? 'primary.main'
                : 'grey.300',
              color: funcs.onlySubscribeSave ? 'white' : 'black'
              // hide if width is less than 530 pixels
              // '@media (max-width: 530px)': {
              //   display: 'none'
              // }
            }}
          />
          <Chip
            label="Favorites"
            size="small"
            icon={
              <ShowIcon
                sx={{
                  color: `${theme.palette.favoriteColor} !important`
                }}
                icon="HeartIcon"
              />
            }
            onClick={() => {
              if (user) {
                funcs.setOnlyFavorites(!funcs.onlyFavorites);
                return;
              }

              showNonLoggedInModal({
                onLoginCBFn: () => {
                  funcs.setOnlyFavorites(!funcs.onlyFavorites);
                },
                modalMessage: 'Please login for us to locate your favorites',
                featureName: 'chip-see-favorites-login-modal'
              });
            }}
            clickable
            color={funcs.onlyFavorites ? 'primary' : 'default'}
            sx={{
              mr: '4px',
              backgroundColor: funcs.onlyFavorites
                ? 'primary.main'
                : 'grey.300',
              color: funcs.onlyFavorites ? 'white' : 'black'
              // hide if width is less than 620 pixels
              // '@media (max-width: 620px)': {
              //   display: 'none'
              // }
            }}
          />
          <Chip
            label="Promotions"
            size="small"
            icon={
              <ShowIcon
                sx={{
                  color: funcs.onlyShowPromotions
                    ? `#c488f7 !important`
                    : isDarkMode
                    ? '#c488f7 !important'
                    : '#6900c2 !important'
                }}
                icon="PaymentsIcon"
              />
            }
            onClick={() =>
              funcs.setOnlyShowPromotions(!funcs.onlyShowPromotions)
            }
            clickable
            color={funcs.onlyShowPromotions ? 'primary' : 'default'}
            sx={{
              mr: '4px',
              backgroundColor: funcs.onlyShowPromotions
                ? 'primary.main'
                : 'grey.300',
              color: funcs.onlyShowPromotions ? 'white' : 'black'
              // hide if width is less than 720 pixels
              // '@media (max-width: 720px)': {
              //   display: 'none'
              // }
            }}
          />
        </Box>
        <span>
          <Button
            onClick={() => setDrawerOpen(true)}
            startIcon={<TuneIcon />}
            sx={{
              ml: 1
            }}
            variant="outlined"
            size="small"
          >
            Filter
          </Button>
          {hasFilters && (
            <IconButton
              size="small"
              onClick={resetAll}
              sx={{
                ml: '8px'
              }}
            >
              <Close />
            </IconButton>
          )}
        </span>
      </Box>
      {showingStr && hasFilters ? (
        <Box mb={1} mt={1}>
          <Typography variant="caption">{showingStr}</Typography>
        </Box>
      ) : null}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            maxWidth: `450px`
          }
        }}
      >
        <Box mb="24px">
          <Button
            variant="outlined"
            onClick={() => setDrawerOpen(false)}
            startIcon={<Close />}
            sx={{ position: 'absolute', right: '8px', top: '8px' }}
            size="small"
          >
            Close
          </Button>
        </Box>
        <FilterDrawer
          funcs={{
            ...funcs
          }}
          latestDealsLoading={latestDealsLoading}
          currentlyShowingDeals={unfilteredDealsWithASIN}
          showingStr={showingStr}
        />
      </Drawer>
    </Box>
  );
};

export default MobileFilter;
